import React, { useEffect, useRef, useState } from "react";
import { playResult, playFanfare, feedbackOn } from "@/lib/feedbackSounds";
import { SoundToggle } from "@/components/SoundToggle";
import { Target, Timer, RotateCcw, ArrowLeft, Volume2 } from "lucide-react";
import { playSequence } from "@/lib/audio";
import { reportScore } from "@/lib/gameScores";

const SPEED_SECS = 30;
const MODES = { round: "10-{u} round", survival: "Keep going until a mistake", speed: "30-second speed challenge" };
const cheer = () => playSequence(["C5", "E5", "G5", "C6"], 0.12, 0.35);
const bestKey = (g, m) => `jt-ng-${g}-${m}`;
const best = (g, m) => Number(localStorage.getItem(bestKey(g, m)) || 0);

export const Btn = ({ onClick, children, testId, primary, tone }) => {
  const toneCls = { right: "bg-green-500 text-green-950 border-green-500", wrong: "bg-red-300 text-red-950 border-red-300", hit: "bg-green-200 text-green-950" }[tone];
  return (
    <button onClick={onClick} data-testid={testId}
      className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${toneCls || (primary ? "bg-primary text-primary-foreground hover:bg-primary/90" : "border border-border hover:border-primary")}`}>{children}</button>
  );
};

export const toneFor = (id, mark) => (mark.reveal?.includes(id) ? "right" : mark.wrong === id ? "wrong" : mark.hit === id ? "hit" : undefined);

const Choices = ({ q, onPick, mark }) => (
  <div className="mt-4 flex flex-wrap gap-2" data-testid="ng-choices">
    {q.choices.map((c) => <Btn key={c} onClick={() => onPick(c)} testId={`ng-choice-${c.replace(/\s+/g, "-").toLowerCase()}`} tone={toneFor(c, mark)}>{q.render ? q.render(c) : c}</Btn>)}
  </div>
);

const Menu = ({ games, game, setGame, start, unit }) => (
  <>
    <p className="mt-1 text-sm text-muted-foreground">Quick warm-ups. Pick a game, then pick how you want to play it.</p>
    <div className="mt-3 flex flex-wrap gap-2">
      {games.map((g) => <Btn key={g.id} primary={game?.id === g.id} onClick={() => setGame(g)} testId={`ng-game-${g.id}`}>{g.label}</Btn>)}
    </div>
    {game && (
      <div className="mt-4 flex flex-wrap gap-2 border-t border-border pt-4" data-testid="ng-modes">
        {Object.entries(MODES).map(([m, l]) => <Btn key={m} onClick={() => start(m)} testId={`ng-start-${m}`}>{l.replace("{u}", unit)}</Btn>)}
      </div>
    )}
  </>
);

const Result = ({ mode, over }) => (
  <p className="text-sm" data-testid="ng-result">
    {mode === "round" ? <>You got <b>{over.final}/10</b> in <b>{over.secs}s</b>.</>
      : mode === "speed" ? <>You got <b>{over.final}</b> in {SPEED_SECS} seconds.</> : <>You got <b>{over.final}</b> in a row.</>}
    {over.isBest && <span className="ml-2 font-semibold text-primary" data-testid="ng-new-best">New best!</span>}
  </p>
);

const useTimers = () => {
  const t = useRef({});
  const clear = () => { clearTimeout(t.current.flash); clearTimeout(t.current.wait); clearTimeout(t.current.play); clearInterval(t.current.tick); };
  useEffect(() => clear, []);
  return [t, clear];
};

// Game engine: each game supplies make(prev) -> { prompt, choices?, check(id), reveal: [ids], play?, Input? }.
export const NoteQuiz = ({ title = "Note games", games, testId = "note-games", unit = "note", where = "", scoreKey }) => {
  const [game, setGame] = useState(null);
  const [mode, setMode] = useState(null);
  const [q, setQ] = useState(null);
  const [n, setN] = useState(0);
  const [score, setScore] = useState(0);
  const [over, setOver] = useState(null);
  const [mark, setMark] = useState({});
  const [left, setLeft] = useState(SPEED_SECS);
  const [t, clearTimers] = useTimers();
  const lock = useRef(false);
  const scoreRef = useRef(0);
  const endAt = useRef(0);
  const t0 = useRef(0);

  const gid = game ? `${scoreKey || testId}-${game.id}` : "";
  const ask = (g, prev) => { prev?.stop?.(); const nq = g.make(prev); setQ(nq); setMark({}); clearTimeout(t.current.play); if (nq.auto) t.current.play = setTimeout(nq.play, 250); };
  const finish = (m, final) => {
    clearInterval(t.current.tick); lock.current = true;
    const isBest = final > best(gid, m);
    const perfect = m === "round" && final >= 10;
    if (isBest) { localStorage.setItem(bestKey(gid, m), String(final)); if (!perfect && feedbackOn()) setTimeout(cheer, 400); }
    reportScore(gid, `${where ? `${where} · ` : ""}${game.label}`, m, final);
    if (perfect) playFanfare(0.35, `${where ? `${where} · ` : ""}${game.label}`);
    setOver({ final, secs: Math.round((Date.now() - t0.current) / 1000), isBest });
  };
  const start = (m) => {
    clearTimers(); lock.current = false; scoreRef.current = 0;
    setMode(m); setN(0); setScore(0); setOver(null); ask(game); t0.current = Date.now();
    if (m === "speed") {
      endAt.current = Date.now() + SPEED_SECS * 1000; setLeft(SPEED_SECS);
      t.current.tick = setInterval(() => {
        const s = Math.max(0, Math.ceil((endAt.current - Date.now()) / 1000));
        setLeft(s);
        if (s <= 0) finish("speed", scoreRef.current);
      }, 200);
    }
  };
  const back = () => { clearTimers(); q?.stop?.(); lock.current = false; setMode(null); };
  const next = (s) => { if (mode === "round" && n + 1 >= 10) return finish(mode, s); setN(n + 1); ask(game, q); };
  const pick = (id) => {
    if (!q || over || lock.current) return;
    const ok = q.check(id);
    playResult(ok);
    const s = score + (ok ? 1 : 0);
    setScore(s); scoreRef.current = s;
    clearTimeout(t.current.flash);
    if (ok) { setMark({ hit: id }); t.current.flash = setTimeout(() => next(s), 250); lock.current = true; t.current.wait = setTimeout(() => { lock.current = false; }, 250); return; }
    if (mode === "speed") { endAt.current -= 2000; setMark({ wrong: id }); t.current.flash = setTimeout(() => setMark({}), 300); return; }
    lock.current = true;
    setMark({ wrong: id, reveal: q.reveal });
    t.current.wait = setTimeout(() => { lock.current = false; if (mode === "survival") finish(mode, s); else next(s); }, 1000);
  };
  useEffect(() => () => q?.stop?.(), [q]);
  const Input = q?.Input;

  return (
    <div className="relative z-10 my-12 rounded-2xl border border-primary/40 bg-card p-4 sm:p-6" data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-2 font-serif text-lg"><Target size={18} className="text-primary" /> {mode ? game.label : title}</p>
        <SoundToggle testId={`${testId}-sound-toggle`} className="ml-auto" />
        {mode && <Btn onClick={back} testId="ng-back"><ArrowLeft size={13} className="mr-1 inline" /> Back</Btn>}
      </div>
      {!mode ? <Menu games={games} game={game} setGame={setGame} start={start} unit={unit} /> : (
        <>
          <p className="mt-2 text-sm text-muted-foreground" data-testid="ng-tip">{game.tip}{mode === "speed" ? " Each wrong answer loses 2 seconds." : ""}</p>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            {over ? <Result mode={mode} over={over} /> : <div data-testid="ng-prompt">{q.prompt}</div>}
            <p className="flex items-center gap-3 text-sm text-muted-foreground">
              <span data-testid="ng-score">{mode === "round" ? `${score}/${Math.min(n + (over ? 1 : 0), 10)} · ${Math.min(n + 1, 10)} of 10` : mode === "speed" ? `Score ${score}` : `Streak ${score}`}</span>
              {mode === "speed" && <span className="font-semibold text-primary" data-testid="ng-timer">{left}s</span>}
              <span className="inline-flex items-center gap-1"><Timer size={13} /> Best: {best(gid, mode)}</span>
            </p>
          </div>
          {!over && q.play && <div className="mt-2"><Btn onClick={q.play} testId="ng-hear"><Volume2 size={13} className="mr-1 inline" /> Hear it</Btn></div>}
          {!over && (Input ? <Input onPick={pick} mark={mark} /> : <Choices q={q} onPick={pick} mark={mark} />)}
          <div className="mt-4 flex flex-wrap gap-2">
            <Btn onClick={() => start(mode)} testId="ng-restart"><RotateCcw size={13} className="mr-1 inline" /> Play again</Btn>
            <Btn onClick={back} testId="ng-change-mode">Change game</Btn>
          </div>
        </>
      )}
    </div>
  );
};
